import React, { useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import classNames from 'classnames';
import {
  Collapse,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  setSelectedGroup,
  updateAssumptionGroup,
  setFieldsAreDirty,
} from 'store/actions';
import { useFocusTextField } from 'utilities/focus';
import { getWithToken } from 'api/network';

const GroupFields = ({
  classes,
  selectedGroup,
  fieldsAreDirty,
  setSelectedGroup,
  updateAssumptionGroup,
  setFieldsAreDirty,
}) => {
  // == useState =========================================

  const [isExpanded, setIsExpanded] = useState(true);
  const [isAddingField, setIsAddingField] = useState(false);
  const [inputRef, setInputFocus] = useFocusTextField();
  const [possibleAssumptionFieldTypes, setPossibleAssumptionFieldTypes] = useState([]);
  const [localFields, setLocalFields] = useState([]);
  const [showLocalError, setShowLocalError] = useState(false);

  // == useEffect =========================================

  async function fetchAssumptionGroupTypes(id) {
    const url = `/api/assumptions/assumptionFieldTypes/${id}`
    const response = await getWithToken(url)
    const possibleValues = await response.json()
    setPossibleAssumptionFieldTypes(possibleValues)
  }

  // == Constants =========================================

  const showNoFields = !selectedGroup?.fields.length && !localFields.length;

  const hasErrors = [...(selectedGroup?.fields ?? []), ...localFields].some(field => !field.name || !field.defaultValue || field.isUniqueName === false);

  // == Event Handlers ===================================

  const onAddNewField = () => {
    setInputFocus();
    if (!possibleAssumptionFieldTypes.length) {
      fetchAssumptionGroupTypes(selectedGroup.groupId);
    }
    const newField = {
      name: '',
      assumptionFieldDataType: 'Number',
      defaultValue: '',
      assumptionFieldType: null,
      isUniqueName: true
    };
    setLocalFields([...localFields, newField]);
    if (!fieldsAreDirty) setFieldsAreDirty(true);
    if (showLocalError) setShowLocalError(false);
  };

  const onFieldNameChange = (index, name) => {
    let isUniqueName = true;
    const searchArray = [...selectedGroup.fields, ...localFields];
    if (searchArray.find(field => field.name.toLowerCase() === name.toLowerCase())) {
      isUniqueName = false;
    } 

    const newLocalFields = [...localFields];
    newLocalFields[index] = {
      ...newLocalFields[index],
      name,
      isUniqueName
    };
    setLocalFields(newLocalFields);
    if (!fieldsAreDirty) setFieldsAreDirty(true);
    if (showLocalError) setShowLocalError(false);
  };

  const onAssumptionFieldPropertyChange = (index, property, value) => {
    const newLocalFields = [...localFields];
    newLocalFields[index] = {
      ...newLocalFields[index],
      [property]: value
    };
    setLocalFields(newLocalFields);
    if (!fieldsAreDirty) setFieldsAreDirty(true);
    if (showLocalError) setShowLocalError(false);
  }

  const onDeleteClicked = (field) => {
    const indexOfField = selectedGroup.fields.indexOf(field);
    const copiedFields = [...selectedGroup.fields];
    copiedFields.splice(indexOfField, 1);

    const updatedGroup = {
      ...selectedGroup,
      fields: copiedFields,
    };

    if (!fieldsAreDirty) setFieldsAreDirty(true);
    setSelectedGroup(updatedGroup);
  };

  const onDeleteLocalClicked = index => {
    const newLocalFields = [...localFields];
    newLocalFields.splice(index, 1);
    setLocalFields(newLocalFields);
    if (!fieldsAreDirty) setFieldsAreDirty(true);
  }

  const onSaveFields = () => {
    if (hasErrors) {
      setShowLocalError(true);
      return;
    }

    const cleanedLocalFields = localFields.map(field => {
      const { isUniqueName, ...rest } = field;
      return rest;
    })

    const copiedFields = [...selectedGroup.fields, ...cleanedLocalFields];

    const updatedGroup = {
      ...selectedGroup,
      fields: copiedFields,
    };
    setSelectedGroup(updatedGroup);
    setFieldsAreDirty(false);
    setLocalFields([]);
    updateAssumptionGroup(updatedGroup);
  };

  const onUpdateDefaultValueChanged = (field, value) => {
    const indexOfField = selectedGroup.fields.indexOf(field);
    const copiedFields = [...selectedGroup.fields];

    const updatedField = {
      ...field,
      defaultValue: value
    }

    copiedFields.splice(indexOfField, 1, updatedField);

    const updatedGroup = {
      ...selectedGroup,
      fields: copiedFields,
    };

    if (!fieldsAreDirty) setFieldsAreDirty(true);
    setSelectedGroup(updatedGroup);
  }

  // == Components ====================================

  return (
    <div>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <IconButton
            className={classes.expandButton}
            style={{ transform: `rotate(${isExpanded ? '0' : '-90'}deg)` }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <ExpandMoreIcon />
          </IconButton>
          <Typography variant='subtitle2' className={classes.name}>
            Fields
          </Typography>
          {fieldsAreDirty && <Typography variant='subtitle2'>{'*'}</Typography>}
        </div>
        <div className={classes.headerRight}>
          <Tooltip title='Save Fields'>
            <span>
              <IconButton
                className={classes.menuButton}
                disabled={!fieldsAreDirty || selectedGroup?.wasPublished}
                onClick={onSaveFields}
              >
                <SaveIcon />
              </IconButton>
            </span>
          </Tooltip>
          {!isAddingField ? (
            <Tooltip title='Add Field'>
              <IconButton
                disabled={selectedGroup?.wasPublished}
                className={classes.menuButton}
                onClick={onAddNewField}
              >
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title='Cancel'>
              <IconButton
                className={classes.menuButton}
                onClick={() => setIsAddingField(false)}
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
      <Collapse in={isExpanded}>
        <div className={classes.container}>
          {showNoFields ? (
            <div className={classes.noFieldsDiv}>
              <Typography className={classes.noFieldsText} variant='body2'>
                Group contains no fields
              </Typography>
            </div>
          ) : (
            <table style={{ width: '100%' }}>
              <thead>
                <tr>
                  <td className={classNames(classes.smallerColumn, classes.textPrimary)}>
                    Name
                  </td>
                  <td className={classNames(classes.smallerColumn, classes.textPrimary)}>
                    Data Type
                  </td>
                  <td className={classNames(classes.smallerColumn, classes.textPrimary)}>
                    Field Type
                  </td>
                  <td className={classNames(classes.largerColumn2, classes.textPrimary)}>
                    Default Value
                  </td>
                  <td className={classes.actionColumn}></td>
                </tr>
              </thead>
              <tbody>
                {selectedGroup?.fields.map(function (field, index) {
                  return (
                    <tr key={index}>
                      <td className={classes.column}>
                        <Typography variant='body2'>{field.name}</Typography>
                      </td>
                      <td className={classes.column}>
                        <Typography variant='body2'>{field.assumptionFieldDataType}</Typography>
                      </td>
                      <td className={classes.column}>
                        <Typography variant='body2'>{field.assumptionFieldTypeDisplay}</Typography>
                      </td>
                      <td className={classes.column}>
                        <TextField
                          value={field.defaultValue}
                          disabled={selectedGroup?.wasPublished}
                          error={field.defaultValue === '' && showLocalError}
                          onChange={(e) => onUpdateDefaultValueChanged(field, e.target.value)}
                          type={field.assumptionFieldDataType === 'Number' ? 'number' : 'text'}
                          inputProps={{
                            className: classes.input,
                            maxLength: 100,
                          }}
                        />
                      </td>
                      <td className={classes.actionColumn}>
                        <IconButton
                          size='small'
                          disabled={selectedGroup?.wasPublished}
                          onClick={() => onDeleteClicked(field)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </td>
                    </tr>
                  );
                })}
                {localFields.map((field, index) => {
                  return (
                    <tr>
                      <td className={classes.column}>
                        <TextField
                          error={!field.isUniqueName || (!field.name && showLocalError)}
                          helperText={!field.isUniqueName ? 'Not unique' : ''}
                          ref={inputRef}
                          value={field.name}
                          inputProps={{
                            className: classes.input,
                            maxLength: 50,
                          }}
                          onChange={(e) => onFieldNameChange(index, e.target.value)}
                        />
                      </td>
                      <td className={classes.column}>
                        <Select
                          value={field.assumptionFieldDataType}
                          name='assumptionFieldDataType'
                          className={classes.typeDropDown}
                          onChange={(e) => onAssumptionFieldPropertyChange(index, 'assumptionFieldDataType', e.target.value)}
                        >
                          <MenuItem
                            value={'Number'}
                            className={classes.typeDropDownItem}
                          >
                            Number
                          </MenuItem>
                          <MenuItem
                            value={'String'}
                            className={classes.typeDropDownItem}
                          >
                            String
                          </MenuItem>
                        </Select>
                      </td>
                      <td className={classes.column}>
                      <Select
                        name='assumptionFieldType'
                        value={field.assumptionFieldType}
                        className={classes.typeDropDown}
                        onChange={(e) => onAssumptionFieldPropertyChange(index, 'assumptionFieldType', e.target.value)}
                      >
                        <MenuItem value={null}>--None</MenuItem>
                        {possibleAssumptionFieldTypes.map(enumValue => {
                          return (
                            <MenuItem value={enumValue.id}>{enumValue.display}</MenuItem>
                          )
                        })}
                      </Select>
                      </td>
                      <td className={classes.column}>
                        <TextField
                          value={field.defaultValue}
                          error={field.defaultValue === '' && showLocalError}
                          onChange={(e) => onAssumptionFieldPropertyChange(index, 'defaultValue', e.target.value)}
                          type={field.assumptionFieldDataType === 'Number' ? 'number' : 'text'}
                          inputProps={{
                            className: classes.input,
                            maxLength: 100,
                          }}
                        />
                      </td>
                      <td className={classes.actionColumn}>
                        <IconButton
                          size='small'
                          disabled={selectedGroup?.wasPublished}
                          onClick={() => onDeleteLocalClicked(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </Collapse>
    </div>
  );
};

// == Styles =========================================

const styles = (theme) => ({
  container: {
    padding: '8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 2px',
    backgroundColor: '#cbcbcb',
    borderBottom: '1px solid #b7b7b7',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    minWidth: 0,
  },
  headerRight: {
    display: 'flex',
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  menuButton: {
    padding: 2,
  },
  noFieldsDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 42,
    margin: '4px 8px',
  },
  noFieldsText: {
    fontStyle: 'italic',
    color: '#a7a9ac',
    fontSize: '.9rem',
  },
  column: {
    width: '30%',
    verticalAlign: 'top',
  },
  smallerColumn: {
    width: '15%',
    verticalAlign: 'top',
  },
  largerColumn2: {
    width: '45%',
    verticalAlign: 'top',
  },
  actionColumn: {
    textAlign: 'right',
    width: '10%',
    verticalAlign: 'top',
  },
  input: {
    fontSize: 14,
  },
  nameInput: {
    height: '21px',
    fontSize: 14,
  },
  textPrimary: {
    fontSize: '.85rem',
    fontWeight: '500',
  },
  typeDropDown: {
    width: '98%',
    fontSize: 14,
  },
  typeDropDownItem: {
    fontSize: 14,
  },
});

// == Connect ==========================================

const mapStateToProps = ({ assumptionGroupsState }) => {
  return {
    selectedGroup: assumptionGroupsState.selectedGroup,
    fieldsAreDirty: assumptionGroupsState.fieldsAreDirty,
  };
};

const mapDispatchToProps = {
  setSelectedGroup,
  updateAssumptionGroup,
  setFieldsAreDirty,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(GroupFields);
